import { lerpColor } from '../../color';
import type { WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const PETALS = 5;
const WAVES = [-0.4, 0, 0.4];
const SHELLS = 8;
const LOBES = 12;
const RAYS = 12;

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const at = (a: number, d: number): readonly [number, number] => [body.x + Math.cos(a) * d * body.scaleX, body.y + Math.sin(a) * d * body.scaleY];
    const line = lerpColor(color, pal.ink, 0.5);
    for (let i = 0; i < PETALS; i++) {
      const a = (i / PETALS) * TAU - Math.PI / 2;
      const [x0, y0] = at(a, r * 0.14);
      const [x1, y1] = at(a, r * 0.62);
      const [ax, ay] = at(a - 0.5, r * 0.42);
      const [bx, by] = at(a + 0.5, r * 0.42);
      g.moveTo(x0, y0).quadraticCurveTo(ax, ay, x1, y1).quadraticCurveTo(bx, by, x0, y0);
    }
    g.stroke({ width: Math.max(1, r * 0.06), color: line, alpha: 0.55 * alpha, join: 'round' });
    g.circle(body.x, body.y, r * 0.07);
    for (let i = 0; i < PETALS; i++) {
      const [x, y] = at(((i + 0.5) / PETALS) * TAU - Math.PI / 2, r * 0.74);
      g.circle(x, y, r * 0.045);
    }
    g.fill({ color: line, alpha: 0.4 * alpha });
    k.terminator(body, lerpColor(color, pal.ink, 0.55), 0.4 * alpha);
    k.rimLight(body, 0.6 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    for (const row of WAVES) {
      const half = Math.sqrt(1 - row * row) * r * 0.8;
      const y0 = body.y + row * r * body.scaleY;
      for (let i = 0; i <= 18; i++) {
        const t = i / 18;
        const x = body.x + (2 * t - 1) * half * body.scaleX;
        const y = y0 + Math.sin(t * TAU * 1.5 + row * 4) * r * 0.075 * body.scaleY;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
    }
    g.stroke({ width: Math.max(1.2, r * 0.09), color: pal.ink, alpha: 0.6 * alpha, cap: 'round', join: 'round' });
    k.terminator(body, lerpColor(fill, pal.heldShade, 0.7), 0.4 * alpha);
    k.rimLight(body, 0.65 * alpha);
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, pal.ink, 0.5), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.08 * alpha });
    const amp = Math.max(1.5, k.unit * 0.008);
    for (let i = 0; i <= 96; i++) {
      const a = (i / 96) * TAU;
      const d = reach + Math.sin(a * LOBES - spin * 2) * amp;
      const x = body.x + Math.cos(a) * d;
      const y = body.y + Math.sin(a) * d;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke({ width: 1.3, color: pal.spot, alpha: 0.55 * alpha, join: 'round' });
    const s = Math.max(3.5, k.unit * 0.024);
    k.ringDots(body, reach, SHELLS, spin, (x, y, _i, a) => {
      const hx = x - Math.cos(a) * s * 0.5;
      const hy = y - Math.sin(a) * s * 0.5;
      g.moveTo(hx, hy).arc(hx, hy, s, a - 0.85, a + 0.85).closePath();
    });
    g.fill({ color: lerpColor(pal.rim, pal.spot, 0.3), alpha: 0.8 * alpha });
    k.ringDots(body, reach, SHELLS, spin, (x, y, _i, a) => {
      const hx = x - Math.cos(a) * s * 0.5;
      const hy = y - Math.sin(a) * s * 0.5;
      for (const d of [-0.45, 0, 0.45]) g.moveTo(hx, hy).lineTo(hx + Math.cos(a + d) * s * 0.9, hy + Math.sin(a + d) * s * 0.9);
    });
    g.stroke({ width: 1, color: pal.ink, alpha: 0.5 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const r = radius * 1.15;
    g.circle(x, y, r);
    if (on) g.fill({ color: last ? pal.spot : lerpColor(pal.rim, pal.incoming.circle, 0.35), alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
    if (on) g.circle(x - r * 0.35, y - r * 0.35, r * 0.28).fill({ color: pal.text, alpha: 0.8 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(7, k.unit * 0.04);
    const sole = Math.max(1.3, k.unit * 0.004);
    let foot = 0;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const ux = dx / len;
      const uy = dy / len;
      const n = Math.min(60, Math.floor(len / step));
      for (let s = 1; s < n; s++) {
        const t = s / n;
        const off = step * 0.28 * (foot++ % 2 === 0 ? 1 : -1);
        const fx = x0 + dx * t - uy * off;
        const fy = y0 + dy * t + ux * off;
        g.circle(fx + ux * sole, fy + uy * sole, sole);
        g.circle(fx - ux * sole, fy - uy * sole, sole * 0.7);
      }
    }
    g.fill({ color: pal.path, alpha: alpha * 2.6 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 2 === 0) g.circle(x, y, size * 0.85);
    else g.regularPoly(x, y, size * 0.6, 4, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const amp = m.unit * 0.012;
    for (let ring = 0; ring < 2; ring++) {
      const rr = m.r * (1 + ring * 0.16);
      for (let i = 0; i <= 72; i++) {
        const a = (i / 72) * TAU;
        const d = rr + Math.sin(a * 9 + m.grow * 4 + ring * 1.7) * amp;
        const x = m.x + Math.cos(a) * d;
        const y = m.y + Math.sin(a) * d;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.stroke({ width: m.width * (ring === 0 ? 1 : 0.6), color: ring === 0 ? pal.incoming.circle : pal.rim, alpha: m.fade * (ring === 0 ? 0.55 : 0.4), join: 'round' });
    }
    const ramp = Math.min(1, m.grow / 0.18);
    const bx = m.x + m.r * 0.85;
    const by = m.y - m.r * 0.85;
    const rb = m.unit * (0.05 + 0.05 * m.grow);
    const tips: number[] = [];
    for (let i = 0; i < RAYS; i++) {
      const a = (i / RAYS) * TAU + 0.2;
      const tx = bx + Math.cos(a) * rb;
      const ty = by + Math.sin(a) * rb + m.grow * rb * 0.25;
      g.moveTo(bx + Math.cos(a) * rb * 0.2, by + Math.sin(a) * rb * 0.2).lineTo(tx, ty);
      tips.push(tx, ty);
    }
    g.stroke({ width: Math.max(1, m.width * 0.45), color: pal.held, alpha: 0.5 * m.fade * ramp, cap: 'round' });
    for (let i = 0; i < tips.length; i += 2) g.circle(tips[i]!, tips[i + 1]!, Math.max(1.2, m.unit * 0.005));
    g.fill({ color: pal.spot, alpha: 0.7 * m.fade * ramp });
  },

  trail(g, x, y, size, angle) {
    g.circle(x, y, size * 0.55);
    g.circle(x + Math.cos(angle + 1.2) * size * 1.2, y + Math.sin(angle + 1.2) * size * 1.2, size * 0.3);
  },
};
